import chroma from 'chroma-js';
import {colorsGlobal} from '../mixins/colors';

export function getTextColor(backgroundColorInput: string) {
    return chroma(backgroundColorInput).luminance() > 0.5 ? colorsGlobal.black : colorsGlobal.white;
}

type RGB = {
    r: number;
    g: number;
    b: number;
};

export const percentualVariants = {
    95: 0.05,
    90: 0.1,
    80: 0.2,
    75: 0.25,
    60: 0.4,
    55: 0.45,
    50: 0.5,
    40: 0.6,
    30: 0.7,
    25: 0.75,
    20: 0.8,
    10: 0.9,
    5: 0.95,
};

export function mixColors(color1: RGB, color2: RGB, percentage: number): RGB {
    const r = Math.round(color1.r * percentage + color2.r * (1 - percentage));
    const g = Math.round(color1.g * percentage + color2.g * (1 - percentage));
    const b = Math.round(color1.b * percentage + color2.b * (1 - percentage));
    return {r, g, b};
}

export function hexToRgb(hex: string): RGB {
    const bigint = parseInt(hex.slice(1), 16);
    return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255,
    };
}

export function rgbToHex({r, g, b}: RGB): string {
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
}

type Color = {
    label: string;
    hex: string;
};

export function generateOverlayColors(overlayedColors: Color[]) {
    // for each whitelabeled color generate all overlayed variants
    for (const color of overlayedColors) {
        setColorVariants(color.hex, color.label, percentualVariants);
    }
}

export function setColorVariants(
    color: string,
    colorPrefix: string,
    variants: Record<string, number>
) {
    const colorRgb = hexToRgb(color);

    for (const variant in variants) {
        // Mix color with white based on the variant percentage
        const whiteRgb = {r: 255, g: 255, b: 255};
        const value = variants[variant];
        const mixedColorHex = rgbToHex(mixColors(colorRgb, whiteRgb, value));
        document.documentElement.style.setProperty(
            `--${colorPrefix}-${variant}-white`,
            mixedColorHex
        );
        // Adding inverse text color for better readability
        document.documentElement.style.setProperty(
            `--${colorPrefix}-${variant}-white-inverse`,
            getTextColor(mixedColorHex)
        );

        // Mix color with black based on the variant percentage
        const blackRgb = {r: 0, g: 0, b: 0};
        const mixedColorBlackHex = rgbToHex(mixColors(colorRgb, blackRgb, value));
        document.documentElement.style.setProperty(
            `--${colorPrefix}-${variant}-black`,
            mixedColorBlackHex
        );
        // Adding inverse text color for better readability
        document.documentElement.style.setProperty(
            `--${colorPrefix}-${variant}-black-inverse`,
            getTextColor(mixedColorBlackHex)
        );

        for (const variant2 in variants) {
            const value2 = variants[variant2];

            // Mix color with white and black based on the variant percentage
            const mixedWithWhiteRgb = hexToRgb(mixedColorHex);
            const mixedColorWhiteAndBlackHex = rgbToHex(
                mixColors(mixedWithWhiteRgb, blackRgb, value2)
            );
            document.documentElement.style.setProperty(
                `--${colorPrefix}-${variant}-white-${variant2}-black`,
                mixedColorWhiteAndBlackHex
            );
            // Adding inverse text color for better readability
            document.documentElement.style.setProperty(
                `--${colorPrefix}-${variant}-white-${variant2}-black-inverse`,
                getTextColor(mixedColorWhiteAndBlackHex)
            );
        }
    }

    // set inverse for default color
    document.documentElement.style.setProperty(`--${colorPrefix}-inverse`, getTextColor(color));
}
